/**
 * AgentEditorTab — Full agent lifecycle management.
 *
 * - View all agents from the graph with their factory status
 * - Create new agents from scratch
 * - Edit any property (name, description, type, model, index, etc.)
 * - Edit system prompts (Prompt Studio) with char/token counts
 * - Edit behavior config (JSON)
 * - Assign/unassign MCP servers and toolsets
 * - Seed prompts from Python source files
 * - Hot-reload agent specs without restart
 * - Test chat inline
 *
 * Uses CSS variables from IndexingDashboard.css for WCAG AA theme support.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, Settings, Save, X, Edit3, ChevronRight,
  ChevronDown, Loader2, CheckCircle, AlertTriangle, Zap,
  Server, FileText, Code, Hash, Type, Eye, EyeOff,
  RotateCcw, Play, Download, Plus, Trash2, Link2, Unlink
} from 'lucide-react';
import type { FactoryAgentSpec, FactorySpecsResponse, GraphMCPServer, GraphToolSet, KnowledgeSource } from './types';
import { API_BASE, authFetch, getAgentColor, getAgentDisplayName } from './constants';
import { Database } from 'lucide-react';

interface AgentEditorTabProps {
  mcpServers: GraphMCPServer[];
  toolSets?: GraphToolSet[];
  onRefresh: () => void;
}

const AGENT_TYPES = ['rag', 'orchestrator', 'mcp_tool', 'pipeline'];

const AGENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  rag: { label: 'RAG', color: '#8b5cf6' },
  orchestrator: { label: 'Orchestrator', color: '#3b82f6' },
  mcp_tool: { label: 'MCP Tool', color: '#14b8a6' },
  pipeline: { label: 'Pipeline', color: '#f59e0b' },
};

// ============================================================================
// Main Component
// ============================================================================

const AgentEditorTab: React.FC<AgentEditorTabProps> = ({ mcpServers, toolSets, onRefresh }) => {
  const [specs, setSpecs] = useState<Record<string, FactoryAgentSpec>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [reloadResult, setReloadResult] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [globalMessage, setGlobalMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Available MCP servers, toolsets, and knowledge sources for pickers
  const [availableMcpServers, setAvailableMcpServers] = useState<string[]>([]);
  const [availableToolSets, setAvailableToolSets] = useState<string[]>([]);
  const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSource[]>([]);

  // Azure OpenAI chat deployments for the Model Override dropdown.
  // Sourced from /api/admin/azure-openai/deployments (super-admin gated).
  // We lazy-fetch and fail open: if the endpoint errors or returns
  // nothing, the dropdown still renders the agent's existing
  // modelOverride value plus the literal it currently holds, so
  // editors aren't blocked from saving an out-of-list value.
  const [modelDeployments, setModelDeployments] = useState<
    { name: string; label: string; description?: string }[]
  >([]);
  const [defaultModel, setDefaultModel] = useState<string>('gpt-4.1');

  const fetchSpecs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/specs`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to fetch factory specs: ${res.status}`);
      const data: FactorySpecsResponse = await res.json();
      setSpecs(data.agents || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSpecs(); }, [fetchSpecs]);

  useEffect(() => {
    setAvailableMcpServers(mcpServers.map(s => s.name));
  }, [mcpServers]);

  useEffect(() => {
    if (toolSets) {
      setAvailableToolSets(toolSets.map(ts => ts.name));
    } else {
      authFetch(`${API_BASE}/admin/graph/toolsets`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(data => setAvailableToolSets((Array.isArray(data) ? data : []).map((ts: { name: string }) => ts.name)))
        .catch(() => {});
    }
  }, [toolSets]);

  useEffect(() => {
    authFetch(`${API_BASE}/admin/graph/sources`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setKnowledgeSources(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    authFetch(`${API_BASE}/admin/azure-openai/deployments`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !Array.isArray(data.deployments)) return;
        setModelDeployments(data.deployments);
        if (typeof data.default === 'string' && data.default) {
          setDefaultModel(data.default);
        }
      })
      .catch(() => {});
  }, []);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setGlobalMessage({ text, type });
    setTimeout(() => setGlobalMessage(null), 5000);
  };

  const handleReload = async () => {
    setReloading(true);
    setReloadResult(null);
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/reload`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setReloadResult(`Reloaded ${data.agents_loaded} agents`);
        await fetchSpecs();
      } else {
        setReloadResult(`Failed: ${data.error || 'Unknown'}`);
      }
    } catch (err) {
      setReloadResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally {
      setReloading(false);
      setTimeout(() => setReloadResult(null), 4000);
    }
  };

  const handleSeedPrompts = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/seed-prompts`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setSeedResult(`Seeded ${data.seeded}/${data.total} agents`);
        await fetchSpecs();
        await handleReload();
      } else {
        setSeedResult(`Failed: ${data.error || 'Unknown'}`);
      }
    } catch (err) {
      setSeedResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally {
      setSeeding(false);
      setTimeout(() => setSeedResult(null), 6000);
    }
  };

  const handleCreateAgent = async (form: Record<string, string | boolean>) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `Failed: ${res.status}`);
      }
      showMessage(`Agent "${form.agentId}" created`, 'success');
      setShowCreateForm(false);
      await fetchSpecs();
      await handleReload();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Create failed', 'error');
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!window.confirm(`Disable agent "${agentId}"? (Soft delete — can be re-enabled)`)) return;
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/${agentId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      showMessage(`Agent "${agentId}" disabled`, 'success');
      await fetchSpecs();
      await handleReload();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Delete failed', 'error');
    }
  };

  const handleUpdateAgent = async (agentId: string, props: Record<string, unknown>) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/${agentId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(props),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      showMessage(`Agent "${agentId}" updated`, 'success');
      await fetchSpecs();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Update failed', 'error');
    }
  };

  const handleLinkMcp = async (agentId: string, mcpName: string) => {
    try {
      await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
          toLabel: 'MCPServer', toKey: 'name', toValue: mcpName,
          relType: 'CONNECTS_TO_MCP',
        }),
      });
      showMessage(`Linked ${agentId} → ${mcpName}`, 'success');
      await fetchSpecs();
      await handleReload();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Link failed', 'error');
    }
  };

  const handleUnlinkMcp = async (agentId: string, mcpName: string) => {
    try {
      await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
          toLabel: 'MCPServer', toKey: 'name', toValue: mcpName,
          relType: 'CONNECTS_TO_MCP',
        }),
      });
      showMessage(`Unlinked ${agentId} ✕ ${mcpName}`, 'success');
      await fetchSpecs();
      await handleReload();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Unlink failed', 'error');
    }
  };

  const handleLinkToolSet = async (agentId: string, tsName: string) => {
    try {
      await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
          toLabel: 'ToolSet', toKey: 'name', toValue: tsName,
          relType: 'USES_TOOLSET',
        }),
      });
      showMessage(`Linked ${agentId} → ${tsName}`, 'success');
      await fetchSpecs();
      await handleReload();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Link failed', 'error');
    }
  };

  const handleLinkSource = async (agentId: string, sourceId: string) => {
    try {
      await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
          toLabel: 'KnowledgeSource', toKey: 'sourceId', toValue: sourceId,
          relType: 'SOURCES_FROM',
        }),
      });
      const source = knowledgeSources.find(s => s.sourceId === sourceId);
      if (source?.indexes?.length) {
        await authFetch(`${API_BASE}/admin/graph/relationships`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
            toLabel: 'SearchIndex', toKey: 'indexId', toValue: source.indexes[0].indexId,
            relType: 'USES_INDEX',
          }),
        });
      }
      showMessage(`Linked ${agentId} to source ${sourceId}`, 'success');
      await fetchSpecs();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Link failed', 'error');
    }
  };

  const handleUnlinkSource = async (agentId: string, sourceId: string) => {
    try {
      await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
          toLabel: 'KnowledgeSource', toKey: 'sourceId', toValue: sourceId,
          relType: 'SOURCES_FROM',
        }),
      });
      showMessage(`Unlinked ${agentId} from source ${sourceId}`, 'success');
      await fetchSpecs();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Unlink failed', 'error');
    }
  };

  const handleUnlinkToolSet = async (agentId: string, tsName: string) => {
    try {
      await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'AIAgent', fromKey: 'agentId', fromValue: agentId,
          toLabel: 'ToolSet', toKey: 'name', toValue: tsName,
          relType: 'USES_TOOLSET',
        }),
      });
      showMessage(`Unlinked ${agentId} ✕ ${tsName}`, 'success');
      await fetchSpecs();
      await handleReload();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Unlink failed', 'error');
    }
  };

  const specsList = Object.values(specs).sort((a, b) => a.name.localeCompare(b.name));
  const factoryReady = specsList.filter(s => s.hasInstructions);
  const notReady = specsList.filter(s => !s.hasInstructions);
  const loadedInRuntimeCount = specsList.filter(s => s.loadedInRuntime !== false).length;
  const notLoadedCount = specsList.length - loadedInRuntimeCount;

  if (loading) {
    return (
      <div className="idx-section">
        <div className="idx-loading"><Loader2 className="idx-spin" size={20} /> Loading factory specs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="idx-section">
        <div className="idx-error">
          <AlertTriangle size={20} /><span>{error}</span>
          <button onClick={fetchSpecs} className="idx-btn idx-btn-sm">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="idx-section">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--idx-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={22} style={{ color: '#f59e0b' }} /> Agent Factory
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--idx-text-muted)', fontSize: 13 }}>
            {specsList.length} agents &middot; {factoryReady.length} factory-ready &middot; {notReady.length} pending
            {notLoadedCount > 0 && (
              <span title="Agents in the graph but not loaded in this env's agent-service runtime — typically missing a DEPLOYED_TO edge to this env.">
                {' '}&middot; <span style={{ color: 'var(--idx-text-warn, #b45309)' }}>{notLoadedCount} not loaded here</span>
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {seedResult && <StatusChip text={seedResult} />}
          {reloadResult && <StatusChip text={reloadResult} />}
          <button onClick={() => setShowCreateForm(true)} className="idx-btn idx-btn-sm" title="Create a new agent">
            <Plus size={14} /> New Agent
          </button>
          <button onClick={handleSeedPrompts} disabled={seeding} className="idx-btn idx-btn-sm" title="Migrate prompts from Python files to Neo4j">
            {seeding ? <Loader2 className="idx-spin" size={14} /> : <Download size={14} />} Seed Prompts
          </button>
          <button onClick={handleReload} disabled={reloading} className="idx-btn idx-btn-primary idx-btn-sm" title="Hot-reload specs from Neo4j">
            {reloading ? <Loader2 className="idx-spin" size={14} /> : <RotateCcw size={14} />} Hot Reload
          </button>
          <button onClick={() => { fetchSpecs(); onRefresh(); }} className="idx-btn idx-btn-sm">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {globalMessage && (
        <div style={{
          padding: '8px 14px', marginBottom: 12, borderRadius: 8,
          background: globalMessage.type === 'error' ? 'var(--idx-bg-error)' : 'var(--idx-bg-success)',
          color: 'var(--idx-text-primary)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
        }}>
          {globalMessage.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />} {globalMessage.text}
        </div>
      )}

      {/* Create Agent Form */}
      {showCreateForm && (
        <CreateAgentForm
          onSubmit={handleCreateAgent}
          onCancel={() => setShowCreateForm(false)}
          existingIds={specsList.map(s => s.agentId)}
        />
      )}

      {/* Agent List */}
      {specsList.length === 0 ? (
        <p style={{ color: 'var(--idx-text-muted)', fontSize: 13, fontStyle: 'italic', padding: 20, textAlign: 'center' }}>
          No agents found in the graph. Create one or run the seed script.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {specsList.map(spec => (
            <AgentCard
              key={spec.agentId}
              spec={spec}
              expanded={expandedAgent === spec.agentId}
              onToggle={() => setExpandedAgent(expandedAgent === spec.agentId ? null : spec.agentId)}
              onUpdate={(props) => handleUpdateAgent(spec.agentId, props)}
              onDelete={() => handleDeleteAgent(spec.agentId)}
              onLinkMcp={(name) => handleLinkMcp(spec.agentId, name)}
              onUnlinkMcp={(name) => handleUnlinkMcp(spec.agentId, name)}
              onLinkToolSet={(name) => handleLinkToolSet(spec.agentId, name)}
              onUnlinkToolSet={(name) => handleUnlinkToolSet(spec.agentId, name)}
              onLinkSource={(id) => handleLinkSource(spec.agentId, id)}
              onUnlinkSource={(id) => handleUnlinkSource(spec.agentId, id)}
              availableMcpServers={availableMcpServers}
              availableToolSets={availableToolSets}
              knowledgeSources={knowledgeSources}
              modelDeployments={modelDeployments}
              defaultModel={defaultModel}
              onShowMessage={showMessage}
              onRefreshSpecs={fetchSpecs}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Create Agent Form
// ============================================================================

const AGENT_STATUS_OPTIONS = ['in_development', 'active', 'uat', 'planned', 'deprecated'];

const CreateAgentForm: React.FC<{
  onSubmit: (form: Record<string, string | boolean>) => void;
  onCancel: () => void;
  existingIds: string[];
}> = ({ onSubmit, onCancel, existingIds }) => {
  const [form, setForm] = useState({
    agentId: '', name: '', description: '', agentType: 'rag',
    hidden: false, status: 'in_development',
  });
  const idError = existingIds.includes(form.agentId) ? 'Agent ID already exists' : '';
  const autoEndpoint = form.agentId ? `/${form.agentId}/chat` : '';
  const autoStreamEndpoint = form.agentId ? `/${form.agentId}/chat/stream` : '';

  return (
    <div style={{
      border: '1px solid var(--idx-border)', borderRadius: 10, padding: 16,
      marginBottom: 16, background: 'var(--idx-bg-secondary)',
    }}>
      <h3 style={{ margin: '0 0 12px', color: 'var(--idx-text-primary)', fontSize: 15 }}>
        <Plus size={16} /> Create New Agent
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={labelStyle}>
          Agent ID *
          <input value={form.agentId} onChange={e => setForm({ ...form, agentId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
            placeholder="my-new-agent" style={inputStyle} />
          {idError && <span style={{ color: '#ef4444', fontSize: 11 }}>{idError}</span>}
        </label>
        <label style={labelStyle}>
          Display Name *
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="My New Agent" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Agent Type
          <select value={form.agentType} onChange={e => setForm({ ...form, agentType: e.target.value })} style={inputStyle}>
            {AGENT_TYPES.map(t => <option key={t} value={t}>{AGENT_TYPE_LABELS[t]?.label || t}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          Status
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
            {AGENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
        <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
          Description
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="What this agent does..." style={inputStyle} />
        </label>
      </div>

      {form.agentId && (
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--idx-bg-inset)', fontSize: 12, color: 'var(--idx-text-muted)' }}>
          <strong style={{ color: 'var(--idx-text-primary)' }}>Auto-generated endpoints:</strong>
          <div style={{ marginTop: 4, fontFamily: 'monospace' }}>
            Chat: {autoEndpoint} &nbsp;|&nbsp; Stream: {autoStreamEndpoint} &nbsp;|&nbsp; Health: /{form.agentId}/health
          </div>
          <div style={{ marginTop: 4, fontSize: 11 }}>
            Streaming: enabled &nbsp;|&nbsp; A2A: enabled &nbsp;|&nbsp; Version: 0.1.0
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--idx-text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.hidden} onChange={e => setForm({ ...form, hidden: e.target.checked })} />
          Hidden from agent list
        </label>
        <div style={{ flex: 1 }} />
        <button onClick={() => onSubmit(form)} disabled={!form.agentId || !form.name || !!idError}
          className="idx-btn idx-btn-primary idx-btn-sm">
          <Plus size={13} /> Create
        </button>
        <button onClick={onCancel} className="idx-btn idx-btn-sm"><X size={13} /> Cancel</button>
      </div>
    </div>
  );
};

// ============================================================================
// Agent Card (expandable)
// ============================================================================

interface AgentCardProps {
  spec: FactoryAgentSpec;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (props: Record<string, unknown>) => void;
  onDelete: () => void;
  onLinkMcp: (name: string) => void;
  onUnlinkMcp: (name: string) => void;
  onLinkToolSet: (name: string) => void;
  onUnlinkToolSet: (name: string) => void;
  onLinkSource: (sourceId: string) => void;
  onUnlinkSource: (sourceId: string) => void;
  availableMcpServers: string[];
  availableToolSets: string[];
  knowledgeSources: KnowledgeSource[];
  /**
   * Azure OpenAI chat deployments + per-environment default model name.
   * Lifted up to AgentEditorTab so the card can render the Model Override
   * dropdown without re-fetching, and so the dropdown options stay
   * consistent across all expanded cards.
   */
  modelDeployments: { name: string; label: string; description?: string }[];
  defaultModel: string;
  onShowMessage: (text: string, type: 'success' | 'error') => void;
  onRefreshSpecs: () => Promise<void>;
}

const AgentCard: React.FC<AgentCardProps> = ({
  spec, expanded, onToggle, onUpdate, onDelete,
  onLinkMcp, onUnlinkMcp, onLinkToolSet, onUnlinkToolSet,
  onLinkSource, onUnlinkSource,
  availableMcpServers, availableToolSets, knowledgeSources,
  modelDeployments, defaultModel,
  onShowMessage, onRefreshSpecs,
}) => {
  const typeConfig = AGENT_TYPE_LABELS[spec.agentType] || { label: spec.agentType, color: '#6f747b' };
  const agentColor = getAgentColor(spec.agentId);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: spec.name, description: spec.description, agentType: spec.agentType,
    modelOverride: spec.modelOverride || '', searchIndexAlias: spec.searchIndexAlias || '',
    endpoint: spec.endpoint || `/${spec.agentId}/chat`,
    streamingEndpoint: spec.streamingEndpoint || `/${spec.agentId}/chat/stream`,
    healthEndpoint: spec.healthEndpoint || `/${spec.agentId}/health`,
    isStreaming: spec.isStreaming !== false,
    supportsA2A: spec.supportsA2A !== false,
    hidden: spec.hidden || false,
    disabled: spec.disabled || false,
    featured: spec.featured || false,
    receivesBroaderContext: spec.receivesBroaderContext || false,
    status: spec.status || 'in_development',
    version: spec.version || '0.1.0',
    introMessage: spec.introMessage || '',
  });

  // Prompt editor
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null) as React.RefObject<HTMLTextAreaElement>;

  // Behavior editor
  const [editingBehavior, setEditingBehavior] = useState(false);
  const [behaviorDraft, setBehaviorDraft] = useState('');

  // Index alias validation
  const [aliasValidation, setAliasValidation] = useState<{ status: 'idle' | 'checking' | 'valid' | 'invalid' | 'error'; detail?: string }>({ status: 'idle' });

  const validateAlias = useCallback(async (alias: string) => {
    if (!alias || !alias.trim()) {
      setAliasValidation({ status: 'idle' });
      return;
    }
    setAliasValidation({ status: 'checking' });
    try {
      const env = (typeof window !== 'undefined' && window.location.hostname.includes('dev')) ? 'dev' : 'lab';
      const res = await authFetch(`${API_BASE}/admin/graph/validate-index?alias=${encodeURIComponent(alias)}&environment=${env}`, { credentials: 'include' });
      const data = await res.json();
      if (data.exists) {
        const info = data.isAlias ? `Alias → ${data.indexName}` : `${data.fields || '?'} fields`;
        setAliasValidation({ status: 'valid', detail: info });
      } else {
        setAliasValidation({ status: 'invalid', detail: `${data.indexName} not found in Azure Search` });
      }
    } catch (err) {
      setAliasValidation({ status: 'error', detail: err instanceof Error ? err.message : 'Check failed' });
    }
  }, []);

  // Test chat
  const [testOpen, setTestOpen] = useState(false);
  const [testQuery, setTestQuery] = useState('');
  const [testResp, setTestResp] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  const loadPrompt = async () => {
    setEditingPrompt(true);
    setPromptLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/${spec.agentId}/instructions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPromptDraft(data.instructions || '');
      }
    } catch { /* empty is fine */ }
    setPromptLoading(false);
    setTimeout(() => promptRef.current?.focus(), 100);
  };

  const savePrompt = async () => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/agents/${spec.agentId}/instructions`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: promptDraft }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      onShowMessage(`Prompt saved (${promptDraft.length} chars)`, 'success');
      setEditingPrompt(false);
      await onRefreshSpecs();
    } catch (err) {
      onShowMessage(`Save failed: ${err instanceof Error ? err.message : 'Unknown'}`, 'error');
    }
  };

  const saveBehavior = async () => {
    try {
      const parsed = JSON.parse(behaviorDraft);
      const res = await authFetch(`${API_BASE}/admin/graph/agents/${spec.agentId}/behavior`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ behaviorConfig: parsed }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      onShowMessage('Behavior saved', 'success');
      setEditingBehavior(false);
      await onRefreshSpecs();
    } catch (err) {
      onShowMessage(`Save failed: ${err instanceof Error ? err.message : 'Unknown'}`, 'error');
    }
  };

  const handleTest = async () => {
    if (!testQuery.trim()) return;
    setTestLoading(true);
    setTestResp('');
    try {
      const res = await authFetch(`${API_BASE}/agents/${spec.agentId}/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: testQuery }),
      });
      const data = await res.json();
      setTestResp(data.response || data.error || 'No response');
    } catch (err) {
      setTestResp(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    } finally { setTestLoading(false); }
  };

  const unlinkedMcps = availableMcpServers.filter(n => !spec.mcpServers.includes(n));
  const unlinkedToolSets = availableToolSets.filter(n => !spec.toolsets.includes(n));
  const linkedSources = knowledgeSources.filter(s => s.agents.includes(spec.agentId));
  const unlinkedSources = knowledgeSources.filter(s => !s.agents.includes(spec.agentId) && s.status !== 'disabled');

  return (
    <div style={{ border: '1px solid var(--idx-border)', borderRadius: 10, background: 'var(--idx-bg-primary)', overflow: 'hidden' }}>
      {/* Header */}
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}>
        {expanded ? <ChevronDown size={16} style={{ color: 'var(--idx-text-muted)' }} /> : <ChevronRight size={16} style={{ color: 'var(--idx-text-muted)' }} />}
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: agentColor, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, color: 'var(--idx-text-primary)', fontSize: 14 }}>{getAgentDisplayName(spec.agentId)}</span>
        <TypeBadge type={spec.agentType} config={typeConfig} />
        {spec.hasInstructions
          ? <span style={badgeStyle('var(--idx-bg-success)')}><CheckCircle size={11} /> {(spec.instructionsLength / 1000).toFixed(1)}k</span>
          : <span style={badgeStyle('var(--idx-bg-warn)')}>No prompt</span>}
        {spec.mcpServers.length > 0 && <span style={{ fontSize: 11, color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}><Server size={11} /> {spec.mcpServers.length}</span>}
        {spec.searchIndexAlias && <span style={{ fontSize: 11, color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}><Hash size={11} /> {spec.searchIndexAlias}</span>}
        {spec.hidden && <span style={badgeStyle('var(--idx-bg-inset)')}><EyeOff size={10} /> Hidden</span>}
        {spec.disabled && <span style={badgeStyle('var(--idx-bg-error)')}>Disabled</span>}
        {spec.isStreaming !== false && <span style={{ fontSize: 10, color: 'var(--idx-text-muted)' }}>stream</span>}
        {/* Deployment scope: which envs this agent is wired up to, and
            whether it's actually loaded in the runtime backing this Express
            instance. Helps admins spot agents missing DEPLOYED_TO edges. */}
        {Array.isArray(spec.deployedEnvironments) && spec.deployedEnvironments.length > 0 && (
          <span
            style={{ fontSize: 10, color: 'var(--idx-text-muted)' }}
            title={`Deployed to: ${spec.deployedEnvironments.join(', ')}`}
          >
            {spec.deployedEnvironments.join('·')}
          </span>
        )}
        {spec.loadedInRuntime === false && (
          <span
            style={badgeStyle('var(--idx-bg-warn)')}
            title="Agent exists in the graph but is not loaded in this env's agent-service runtime. Likely missing a DEPLOYED_TO edge to the runtime's environment."
          >
            Not loaded here
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--idx-text-faint)' }}>{spec.agentId}</span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--idx-border)', padding: '14px' }}>

          {/* Properties Section */}
          {editMode ? (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <label style={labelStyle}>Name <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={inputStyle} /></label>
                <label style={labelStyle}>Type <select value={editForm.agentType} onChange={e => setEditForm({ ...editForm, agentType: e.target.value })} style={inputStyle}>{AGENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></label>
                <label style={labelStyle}>Status <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} style={inputStyle}>{AGENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}</select></label>
              </div>
              <label style={labelStyle}>Description <input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} style={{ ...inputStyle, width: '100%' }} /></label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
                <label style={labelStyle}>Chat Endpoint <input value={editForm.endpoint} onChange={e => setEditForm({ ...editForm, endpoint: e.target.value })} style={inputStyle} /></label>
                <label style={labelStyle}>Streaming Endpoint <input value={editForm.streamingEndpoint} onChange={e => setEditForm({ ...editForm, streamingEndpoint: e.target.value })} style={inputStyle} /></label>
                <label style={labelStyle}>Health Endpoint <input value={editForm.healthEndpoint} onChange={e => setEditForm({ ...editForm, healthEndpoint: e.target.value })} style={inputStyle} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
                <label style={labelStyle}>Model Override
                  <select
                    value={editForm.modelOverride}
                    onChange={e => setEditForm({ ...editForm, modelOverride: e.target.value })}
                    style={inputStyle}
                    title={`Default for this environment is "${defaultModel}". Leave blank to use it.`}
                  >
                    <option value="">Default ({defaultModel})</option>
                    {modelDeployments.map(d => (
                      <option key={d.name} value={d.name}>
                        {d.label || d.name}{d.description ? ` — ${d.description}` : ''}
                      </option>
                    ))}
                    {/* Preserve an out-of-list legacy value so saving an
                        agent whose modelOverride predates the dropdown
                        list doesn't silently drop the override. */}
                    {editForm.modelOverride &&
                      !modelDeployments.some(d => d.name === editForm.modelOverride) && (
                      <option value={editForm.modelOverride}>
                        {editForm.modelOverride} (legacy / not in current list)
                      </option>
                    )}
                  </select>
                </label>
                <label style={labelStyle}>Version <input value={editForm.version} onChange={e => setEditForm({ ...editForm, version: e.target.value })} placeholder="0.1.0" style={inputStyle} /></label>
                <label style={labelStyle}>
                  Search Index Alias
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input value={editForm.searchIndexAlias} onChange={e => { setEditForm({ ...editForm, searchIndexAlias: e.target.value }); setAliasValidation({ status: 'idle' }); }} placeholder="askaldo-agent-{env}" style={{ ...inputStyle, flex: 1 }} />
                    <button type="button" onClick={() => validateAlias(editForm.searchIndexAlias)} className="idx-btn idx-btn-sm"
                      style={{ whiteSpace: 'nowrap', padding: '4px 8px', fontSize: 11 }}
                      disabled={aliasValidation.status === 'checking' || !editForm.searchIndexAlias}>
                      {aliasValidation.status === 'checking' ? <Loader2 size={11} className="idx-spin" /> : 'Check'}
                    </button>
                  </div>
                  {aliasValidation.status === 'valid' && (
                    <span style={{ fontSize: 11, color: 'var(--idx-text-success, #10b981)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                      <CheckCircle size={11} /> Exists — {aliasValidation.detail}
                    </span>
                  )}
                  {aliasValidation.status === 'invalid' && (
                    <span style={{ fontSize: 11, color: 'var(--idx-accent-warn, #f59e0b)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                      <AlertTriangle size={11} /> {aliasValidation.detail}
                    </span>
                  )}
                  {aliasValidation.status === 'error' && (
                    <span style={{ fontSize: 11, color: 'var(--idx-text-error, #ef4444)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                      <AlertTriangle size={11} /> {aliasValidation.detail}
                    </span>
                  )}
                </label>
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                {([
                  ['isStreaming', 'Streaming'],
                  ['supportsA2A', 'A2A Protocol'],
                  ['hidden', 'Hidden'],
                  ['disabled', 'Disabled'],
                  ['featured', 'Featured'],
                  ['receivesBroaderContext', 'Receives Broader Context'],
                ] as const).map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--idx-text-muted)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={editForm[key] as boolean} onChange={e => setEditForm({ ...editForm, [key]: e.target.checked })} />
                    {label}
                  </label>
                ))}
              </div>

              <label style={{ ...labelStyle, marginTop: 8 }}>
                Intro Message
                <textarea value={editForm.introMessage} onChange={e => setEditForm({ ...editForm, introMessage: e.target.value })}
                  placeholder="Welcome message shown when agent is selected..." rows={3}
                  style={{ ...inputStyle, resize: 'vertical' as const, fontFamily: 'inherit' }} />
              </label>

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => { onUpdate(editForm); setEditMode(false); }} className="idx-btn idx-btn-primary idx-btn-sm"><Save size={13} /> Save</button>
                <button onClick={() => setEditMode(false)} className="idx-btn idx-btn-sm"><X size={13} /> Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <InfoBox icon={<Type size={14} />} label="Description" value={spec.description || '—'} />
                <InfoBox icon={<Code size={14} />} label="Endpoint" value={spec.endpoint || `/${spec.agentId}/chat`} />
                <InfoBox icon={<Zap size={14} />} label="Streaming" value={spec.streamingEndpoint || `/${spec.agentId}/chat/stream`} />
                <InfoBox icon={<Code size={14} />} label="Model" value={spec.modelOverride || `Default (${defaultModel})`} />
                <InfoBox icon={<Hash size={14} />} label="Search Index" value={spec.searchIndexAlias || '—'} />
                <InfoBox icon={<FileText size={14} />} label="Prompt" value={spec.hasInstructions ? `${spec.instructionsLength.toLocaleString()} chars (~${Math.round(spec.instructionsLength / 4).toLocaleString()} tokens)` : 'Not set'} />
                <InfoBox icon={<Hash size={14} />} label="Version" value={spec.version || '—'} />
                <InfoBox icon={<Settings size={14} />} label="Status" value={spec.status?.replace(/_/g, ' ') || '—'} />
              </div>
            </div>
          )}

          {/* Behavior badges */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            <BehaviorBadge label="Escalation" enabled={spec.behavior.escalationEnabled} />
            <BehaviorBadge label="Citations" enabled={spec.behavior.citationsEnabled} />
            <BehaviorBadge label="Widget Block" enabled={spec.behavior.escalationWidgetBlocking} />
            {spec.behavior.linkRewriting.length > 0 && <span style={badgeStyle('var(--idx-bg-inset)')}>{spec.behavior.linkRewriting.length} rewrite rules</span>}
          </div>

          {/* MCP Servers */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--idx-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><Server size={13} /> MCP Servers</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {spec.mcpServers.map(name => (
                <span key={name} style={{ ...badgeStyle('var(--idx-bg-inset)'), gap: 4 }}>
                  {name}
                  <button onClick={(e) => { e.stopPropagation(); onUnlinkMcp(name); }} style={unlinkBtnStyle} title="Unlink"><Unlink size={10} /></button>
                </span>
              ))}
              {unlinkedMcps.length > 0 && (
                <select onChange={e => { if (e.target.value) onLinkMcp(e.target.value); e.target.value = ''; }} defaultValue="" style={{ ...inputStyle, fontSize: 11, padding: '2px 6px', width: 'auto' }}>
                  <option value="">+ Add MCP...</option>
                  {unlinkedMcps.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
              {spec.mcpServers.length === 0 && unlinkedMcps.length === 0 && <span style={{ fontSize: 11, color: 'var(--idx-text-faint)' }}>None available</span>}
            </div>
          </div>

          {/* Toolsets */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--idx-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><Settings size={13} /> Toolsets</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {spec.toolsets.map(name => (
                <span key={name} style={{ ...badgeStyle('var(--idx-bg-inset)'), gap: 4 }}>
                  {name}
                  <button onClick={(e) => { e.stopPropagation(); onUnlinkToolSet(name); }} style={unlinkBtnStyle} title="Unlink"><Unlink size={10} /></button>
                </span>
              ))}
              {unlinkedToolSets.length > 0 && (
                <select onChange={e => { if (e.target.value) onLinkToolSet(e.target.value); e.target.value = ''; }} defaultValue="" style={{ ...inputStyle, fontSize: 11, padding: '2px 6px', width: 'auto' }}>
                  <option value="">+ Add Toolset...</option>
                  {unlinkedToolSets.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
            </div>
          </div>

          {/* Knowledge Sources */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--idx-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><Database size={13} /> Knowledge Sources</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {linkedSources.map(src => (
                <span key={src.sourceId} style={{ ...badgeStyle(src.status === 'active' ? '#dcfce7' : 'var(--idx-bg-inset)'), gap: 4 }}>
                  {src.displayName}
                  {src.indexes.length > 0 && <span style={{ fontSize: 10, color: 'var(--idx-text-muted)' }}>({src.indexes.map(i => i.indexId).join(', ')})</span>}
                  {src.indexes.some(i => i.documentCount != null) && (
                    <span style={{ fontSize: 10, color: 'var(--idx-text-muted)' }}>
                      {src.indexes.filter(i => i.documentCount != null).map(i => `${i.documentCount?.toLocaleString()} docs`).join(', ')}
                    </span>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); onUnlinkSource(src.sourceId); }} style={unlinkBtnStyle} title="Unlink"><Unlink size={10} /></button>
                </span>
              ))}
              {linkedSources.length === 0 && <span style={{ fontSize: 11, color: 'var(--idx-text-faint)' }}>No sources linked</span>}
              {unlinkedSources.length > 0 && (
                <select onChange={e => { if (e.target.value) onLinkSource(e.target.value); e.target.value = ''; }} defaultValue="" style={{ ...inputStyle, fontSize: 11, padding: '2px 6px', width: 'auto' }}>
                  <option value="">+ Add Source...</option>
                  {unlinkedSources.map(s => <option key={s.sourceId} value={s.sourceId}>{s.displayName} ({s.sourceId})</option>)}
                </select>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {!editMode && <button onClick={() => setEditMode(true)} className="idx-btn idx-btn-sm"><Edit3 size={13} /> Edit Properties</button>}
            <button onClick={loadPrompt} className="idx-btn idx-btn-sm"><FileText size={13} /> Edit Prompt</button>
            <button onClick={() => { setEditingBehavior(true); setBehaviorDraft(JSON.stringify(spec.behavior, null, 2)); }} className="idx-btn idx-btn-sm"><Settings size={13} /> Edit Behavior</button>
            {spec.hasInstructions && <button onClick={() => setTestOpen(true)} className="idx-btn idx-btn-primary idx-btn-sm"><Play size={13} /> Test Chat</button>}
            <button onClick={onDelete} className="idx-btn idx-btn-sm" style={{ marginLeft: 'auto' }}><Trash2 size={13} /> Disable</button>
          </div>

          {/* Prompt Editor */}
          {editingPrompt && (
            <EditorPanel title="System Prompt" subtitle={`${promptDraft.length.toLocaleString()} chars · ~${Math.round(promptDraft.length / 4).toLocaleString()} tokens`}
              onSave={savePrompt} onCancel={() => setEditingPrompt(false)}>
              {promptLoading ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--idx-text-muted)' }}><Loader2 className="idx-spin" size={16} /> Loading...</div> : (
                <textarea ref={promptRef} value={promptDraft} onChange={e => setPromptDraft(e.target.value)} style={textareaStyle} />
              )}
            </EditorPanel>
          )}

          {/* Behavior Editor */}
          {editingBehavior && (
            <EditorPanel title="Behavior Config (JSON)" onSave={saveBehavior} onCancel={() => setEditingBehavior(false)}>
              <textarea value={behaviorDraft} onChange={e => setBehaviorDraft(e.target.value)} style={{ ...textareaStyle, minHeight: 200 }} />
            </EditorPanel>
          )}

          {/* Test Chat */}
          {testOpen && (
            <EditorPanel title={`Test — ${getAgentDisplayName(spec.agentId)}`} onCancel={() => setTestOpen(false)}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={testQuery} onChange={e => setTestQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTest()}
                  placeholder="Type a test question..." style={{ ...inputStyle, flex: 1 }} />
                <button onClick={handleTest} disabled={testLoading || !testQuery.trim()} className="idx-btn idx-btn-primary idx-btn-sm">
                  {testLoading ? <Loader2 className="idx-spin" size={13} /> : <Play size={13} />} Send
                </button>
              </div>
              {testResp && <div style={{ padding: 10, borderRadius: 6, fontSize: 13, lineHeight: 1.5, background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)', maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>{testResp}</div>}
            </EditorPanel>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Shared sub-components
// ============================================================================

const EditorPanel: React.FC<{
  title: string; subtitle?: string;
  onSave?: () => void; onCancel: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, onSave, onCancel, children }) => (
  <div style={{ border: '1px solid var(--idx-border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--idx-bg-secondary)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--idx-text-primary)' }}>{title}</span>
      {subtitle && <span style={{ fontSize: 11, color: 'var(--idx-text-muted)' }}>{subtitle}</span>}
    </div>
    {children}
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      {onSave && <button onClick={onSave} className="idx-btn idx-btn-primary idx-btn-sm"><Save size={13} /> Save</button>}
      <button onClick={onCancel} className="idx-btn idx-btn-sm"><X size={13} /> {onSave ? 'Cancel' : 'Close'}</button>
    </div>
  </div>
);

const InfoBox: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div style={{ fontSize: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, marginBottom: 2, color: 'var(--idx-text-muted)' }}>{icon} {label}</div>
    <div style={{ color: 'var(--idx-text-primary)', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

const BehaviorBadge: React.FC<{ label: string; enabled: boolean }> = ({ label, enabled }) => (
  <span style={{
    ...badgeStyle(enabled ? 'var(--idx-bg-success)' : 'var(--idx-bg-secondary)'),
    gap: 3,
  }}>
    {enabled ? <Eye size={11} /> : <EyeOff size={11} />} {label}
  </span>
);

const TypeBadge: React.FC<{ type: string; config: { label: string; color: string } }> = ({ config }) => (
  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: config.color + '22', color: config.color, fontWeight: 600 }}>
    {config.label}
  </span>
);

const StatusChip: React.FC<{ text: string }> = ({ text }) => (
  <span style={{
    fontSize: 12, padding: '4px 10px', borderRadius: 6, color: 'var(--idx-text-primary)',
    background: text.includes('fail') || text.includes('Error') ? 'var(--idx-bg-error)' : 'var(--idx-bg-success)',
  }}>
    {text}
  </span>
);

// ============================================================================
// Shared styles
// ============================================================================

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '6px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--idx-border)', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)',
  marginTop: 2,
};

const textareaStyle: React.CSSProperties = {
  width: '100%', minHeight: 300, maxHeight: 600, fontFamily: 'monospace',
  fontSize: 12, lineHeight: 1.5, padding: 10, borderRadius: 6,
  border: '1px solid var(--idx-border)', background: 'var(--idx-bg-primary)',
  color: 'var(--idx-text-primary)', resize: 'vertical' as const,
};

const labelStyle: React.CSSProperties = { color: 'var(--idx-text-muted)', fontSize: 12, display: 'block' };

const badgeStyle = (bg: string): React.CSSProperties => ({
  fontSize: 11, padding: '2px 8px', borderRadius: 4,
  background: bg, color: 'var(--idx-text-primary)',
  display: 'inline-flex', alignItems: 'center',
});

const unlinkBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center',
};

export { AgentEditorTab };
export default AgentEditorTab;
